import type { Role, SignalMessage } from './types';
import type { SignalClient } from './signal';

const STUN_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];

/** Fetch Cloudflare Calls TURN credentials from our Worker. Returns
 *  an iceServers list (STUN baseline + TURN if configured). When the
 *  Worker has no TURN secrets, /api/turn returns { iceServers: [] }
 *  and we just use STUN. Network errors fall back to STUN too — TURN
 *  is "best effort upgrade" rather than required. */
async function loadIceServers(): Promise<RTCIceServer[]> {
  const baseline = [...STUN_SERVERS];
  try {
    const res = await fetch('/api/turn');
    if (!res.ok) return baseline;
    const data = (await res.json()) as {
      iceServers?: RTCIceServer | RTCIceServer[];
    };
    if (!data.iceServers) return baseline;
    // Cloudflare returns a single object with multiple urls; older shapes
    // may return an array. Handle both.
    const turn = Array.isArray(data.iceServers) ? data.iceServers : [data.iceServers];
    return [...baseline, ...turn];
  } catch {
    return baseline;
  }
}

export interface PeerHandlers {
  onChannelOpen(dc: RTCDataChannel): void;
  onChannelMessage(data: string | ArrayBuffer): void;
  onChannelClose(): void;
  onConnectionFailed?(): void;
}

export interface Peer {
  /** Attach the signaling client so the peer can send offer/answer/ICE. */
  attach(signal: SignalClient): void;
  /**
   * Host-only: create the WebRTC offer and send it. Call this once both
   * peers are present (i.e. on `peer-ready` from the signaling DO). On
   * the guest, this is a no-op.
   */
  negotiate(): Promise<void>;
  handleSignal(msg: SignalMessage): Promise<void>;
  send(data: string | ArrayBuffer): void;
  close(): void;
}

export async function createPeer(role: Role, h: PeerHandlers): Promise<Peer> {
  const iceServers = await loadIceServers();
  console.log(`[peer:${role}] iceServers:`, iceServers.length, 'entries');
  const pc = new RTCPeerConnection({ iceServers });
  let dc: RTCDataChannel | null = null;
  let signal: SignalClient | null = null;

  pc.addEventListener('icecandidate', (ev) => {
    if (ev.candidate && signal) {
      console.log(`[peer:${role}] local ICE → send`, ev.candidate.candidate);
      signal.send({ type: 'ice', candidate: ev.candidate.toJSON() });
    } else if (!ev.candidate) {
      console.log(`[peer:${role}] local ICE gathering complete`);
    }
  });

  pc.addEventListener('iceconnectionstatechange', () => {
    console.log(`[peer:${role}] iceConnectionState=${pc.iceConnectionState}`);
  });

  pc.addEventListener('connectionstatechange', () => {
    console.log(`[peer:${role}] connectionState=${pc.connectionState}`);
    if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
      h.onConnectionFailed?.();
    }
  });

  pc.addEventListener('signalingstatechange', () => {
    console.log(`[peer:${role}] signalingState=${pc.signalingState}`);
  });

  function wireChannel(channel: RTCDataChannel) {
    dc = channel;
    channel.binaryType = 'arraybuffer';
    channel.addEventListener('open', () => {
      console.log(`[peer:${role}] dc OPEN`);
      h.onChannelOpen(channel);
    });
    channel.addEventListener('message', (ev) => h.onChannelMessage(ev.data));
    channel.addEventListener('close', () => {
      console.log(`[peer:${role}] dc CLOSE`);
      h.onChannelClose();
    });
    channel.addEventListener('error', (e) => {
      console.log(`[peer:${role}] dc ERROR`, e);
    });
  }

  if (role === 'host') {
    // Host creates the channel; "game" is the negotiated label.
    const channel = pc.createDataChannel('game', { ordered: false, maxRetransmits: 0 });
    wireChannel(channel);
  } else {
    pc.addEventListener('datachannel', (ev) => wireChannel(ev.channel));
  }

  return {
    attach(s) {
      signal = s;
    },

    async negotiate() {
      if (role !== 'host' || !signal) {
        console.log(`[peer:${role}] negotiate ignored (role=${role} signal=${!!signal})`);
        return;
      }
      console.log(`[peer:${role}] creating offer`);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log(`[peer:${role}] sending offer (sdp ${offer.sdp?.length} chars)`);
      signal.send({ type: 'offer', sdp: offer.sdp ?? '' });
    },

    async handleSignal(msg) {
      console.log(`[peer:${role}] handleSignal type=${msg.type}`);
      if (msg.type === 'offer') {
        await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        console.log(`[peer:${role}] sending answer`);
        signal?.send({ type: 'answer', sdp: answer.sdp ?? '' });
      } else if (msg.type === 'answer') {
        await pc.setRemoteDescription({ type: 'answer', sdp: msg.sdp });
      } else if (msg.type === 'ice') {
        try {
          await pc.addIceCandidate(msg.candidate);
        } catch (e) {
          console.log(`[peer:${role}] addIceCandidate failed`, e);
        }
      }
    },

    send(data) {
      if (dc && dc.readyState === 'open') dc.send(data as string);
    },

    close() {
      try { dc?.close(); } catch { /* ignore */ }
      try { pc.close(); } catch { /* ignore */ }
    },
  };
}
